import hallAdapter from '@/adapters/hall';
import eventAdapter from '@/adapters/event';
import orderAdapter from '@/adapters/order';
import schemeAdapter from '@/adapters/scheme';
import performanceAdapter from '@/adapters/performance';

export default {
  namespaced: true,
  state: {
    event: null,
    buyer: null,
    order: null,
    tickets: [],
    availableSeats: [],
    isOrderPaid: false,
  },
  getters: {
    sum(state) {
      return state.tickets.reduce((acc, el) => acc + el.price, 0);
    },
    ticketsCount(state) {
      return state.tickets.length;
    },
    isOrderActive(state) {
      if (!state.order) return false;
      return state.order.expiration > Date.now();
    },
    soldOutIds(state) {
      return state.availableSeats
        .filter((el) => el.seatsCount === 0)
        .map((el) => el.id);
    },
  },
  mutations: {
    setOrderPayStatus(state, status) {
      state.isOrderPaid = status;
    },
    setEvent(state, event) {
      state.event = event;
    },
    setBuyer(state, buyer) {
      state.buyer = buyer;
    },
    setOrder(state, order) {
      state.order = order;
    },
    setTickets(state, tickets) {
      state.tickets = tickets;
    },
    clearEvent(state) {
      state.event = null;
    },
    clearBuyer(state) {
      state.buyer = null;
    },
    clearOrder(state) {
      state.order = null;
    },
    clearTickets(state) {
      state.tickets = [];
    },
    removeTicketById(state, id) {
      state.tickets = state.tickets.filter((el) => el.id !== id);
    },
    addAvailableSeatsInfo(state, event) {
      // Фильтруем прошедшие события и старую информацию, если есть
      state.availableSeats = state.availableSeats
        .filter((el) => el.id !== event.id && el.expiration >= Date.now());
      state.availableSeats.push(event);
    },
  },
  actions: {
    fetchHalls() {
      return this.$axios.get('/halls')
        .then(({ data }) => (data.items || []).map(hallAdapter));
    },
    fetchSchemes() {
      return this.$axios.get('/schemes')
        .then(({ data }) => (data.items || []).map(schemeAdapter));
    },
    fetchEvents(ctx, params) {
      return this.$axios.get('/events', { params })
        .then(({ data }) => (data.items || []).map(eventAdapter));
    },
    fetchPerformances() {
      return this.$axios.get('/performances')
        .then(({ data }) => (data.items || []).map(performanceAdapter));
    },
    fetchPerformanceById(ctx, id) {
      return this.$axios.get(`/performances/${id}`)
        .then(({ data }) => performanceAdapter(data));
    },
    fetchEventById(ctx, id) {
      return this.$axios.get(`/events/${id}`)
        .then(({ data }) => eventAdapter(data));
    },
    fetchSchemeById(ctx, id) {
      return this.$axios.get(`/schemes/${id}`)
        .then(({ data }) => schemeAdapter(data));
    },
    async fetchAvailableSeatsByEventId({ dispatch }, eventId) {
      const { availableSeats } = await dispatch('fetchEventById', eventId);
      return availableSeats;
    },
    async fetchCompiledData() {
      const { data } = await this.$axios.get('/compiled/buildings/1');
      return {
        halls: (data.hallItemList || []).map(hallAdapter),
        events: (data.eventItemList || []).map(eventAdapter),
        schemes: (data.schemeItemList || []).map(schemeAdapter),
        performances: (data.performanceItemList || []).map(performanceAdapter),
      };
    },
    async fetchPastEvents({ dispatch }) {
      const { events, performances } = await dispatch('fetchCompiledData');
      return events
        .filter((el) => el.date <= Date.now())
        .map((event) => {
          const performance = performances.find((el) => el.id === event.performanceId);
          return {
            ...event,
            performance,
          };
        });
    },
    async fetchAviableEvents({ dispatch }) {
      const {
        events, performances, halls, schemes,
      } = await dispatch('fetchCompiledData');
      return events
        .filter((el) => el.date >= Date.now())
        .map((event) => {
          const performance = performances.find((el) => el.id === event.performanceId);
          const scheme = schemes.find((el) => el.id === event.schemeId);
          const hall = scheme && halls.find((el) => el.id === scheme.hallId);
          return {
            ...event,
            performance,
            scheme,
            hall,
          };
        });
    },
    fetchOrderById(ctx, id) {
      return this.$axios.get(`/orders/${id}`)
        .then(({ data }) => orderAdapter(data));
    },
    fetchOrderByLink(ctx, orderLink) {
      return this.$axios.get(`/orderlinks/${orderLink}`)
        .then(({ data }) => JSON.parse(data.order_data));
    },
    fetchRefundStatus() {
      return this.$axios.get('/sbpilot/e/describe')
        .then(({ data }) => {
          if (data.found) {
            return data.code;
          }
          throw new Error();
        });
    },
    startRefund(ctx, { orderId, orderLink, orderSum }) {
      return this.$axios.post(`/orderlinks/${orderLink}/refund`, {
        amount: orderSum,
        order_id: orderId,
      });
    },
    createOrder(ctx, { tickets, buyer }) {
      const order = {
        user_details: {
          name: buyer?.name,
          email: buyer?.email,
          phone: buyer?.phone.replace(/\D/g, '').substr(1),
        },
        tickets: tickets.map((el) => ({
          hall_scheme_seat_id: Number(el.seatId),
          event_id: Number(el.eventId),
        })),
      };
      return this.$axios.post('/orders/create', { ...order })
        .then(({ data }) => orderAdapter(data));
    },
    cancelOrderById(ctx, { id, isRefund = false }) {
      return this.$axios.delete(`/orders/${id}`, {
        data: {
          process_refund: isRefund,
        },
      });
    },
    confirmOrderPayment(ctx, { order, amount }) {
      return this.$axios.put(`/orders/${order.id}/payment`, {
        order_data: JSON.stringify(order),
        order_amount: amount,
      });
    },
    fetchTicketByOrderId(ctx, orderId) {
      return this.$axios.get(`/orders/${orderId}/ticket`)
        .then(({ data }) => data.filepath);
    },
    tryToCancelOrder({ state, getters, dispatch }) {
      return getters.isOrderActive && !state.isOrderPaid
        ? dispatch('cancelOrderById', { id: state.order.id, isRefund: false })
        : Promise.resolve();
    },
    clearAll({ commit }) {
      commit('clearEvent');
      commit('clearBuyer');
      commit('clearOrder');
      commit('clearTickets');
      commit('setOrderPayStatus', false);
    },
  },
};
